import { Component } from '@angular/core';
import { Store, createSelector } from '@ngrx/store';
import { QuillEditorComponent, QuillModules } from 'ngx-quill';

import { FreyaCommonModule } from 'src/app/freya-common/freya-common.module';
import { JOB_CREATE_INDEXES, JOB_SUMMARIES, jobSummaryDefaultToolbar } from 'src/app/global.constants';
import { LibModule } from 'src/app/lib.ts/lib.module';
import { QuillTemplateVariableToolbarDirective } from 'src/app/shared/quill-template-variable.directive';
import { SharedModule } from 'src/app/shared/shared.module';

import { QuillTemplateLocation } from '../../../generated/graphql.generated';
import { ColorSchemeService } from '../../color-scheme.service';
import { isDeltaBlank } from '../../job/job-activity/comments.utils';
import { JobSummaryActions } from '../../job/job-create/job-create-summary/job-create-summary.actions';
import { jobCreateSummaryFeature } from '../../job/job-create/job-create-summary/job-create-summary.feature';
import { SummaryType } from '../../job/job-create/job-create-summary/job-create-summary.reducer';
import { selectSummaryIndex } from '../../job/job-tool.reducer';

import { NewOpportunityActions } from '../new-opportunity.actions';

const selectSummaryEditorValues = createSelector(
    jobCreateSummaryFeature.selectSummaryEditors,
    (editors) => {
        const { crewSummary, adminSummary, customerSummary } = editors;
        return { crewSummary, adminSummary, customerSummary };
    },
);

@Component({
    selector: 'app-new-opportunity-summary',
    standalone: true,
    imports: [
        FreyaCommonModule,
        LibModule,
        SharedModule,
        QuillEditorComponent,
        QuillTemplateVariableToolbarDirective,
    ],
    templateUrl: './new-opportunity-summary.component.html',
    styleUrls: [
      './new-opportunity-summary.component.scss',
      '../../job/colors.scss',
    ],
})
export class NewOpportunitySummaryComponent {
    constructor(
        public store: Store,
        public colorScheme: ColorSchemeService,
    ) {}

    public editModules: QuillModules = {
        toolbar: jobSummaryDefaultToolbar,
        variableToolbar: true,
        templateToolbar: true,
        keyboard: {
            bindings: {
                submit: {
                    key: 'Enter',
                    ctrlKey: true,
                    handler: () => {
                        this._moveBackToSummaryTab();
                    },
                },
            },
        },
    };

    public jobSummaries = JOB_SUMMARIES;

    public jobCreateIndexes = JOB_CREATE_INDEXES;

    public variablesLocation = QuillTemplateLocation.JobSummary;

    public templatesLocations: Record<typeof JOB_SUMMARIES[number]['key'], QuillTemplateLocation[]> = {
      customerSummary: [ QuillTemplateLocation.JobSummary, QuillTemplateLocation.JobCustomerSummary ],
      adminSummary: [ QuillTemplateLocation.JobSummary, QuillTemplateLocation.JobAdminSummary ],
      crewSummary: [ QuillTemplateLocation.JobSummary, QuillTemplateLocation.JobCrewSummary ],
    };

    public summaryLabels = JOB_SUMMARIES.reduce((acc, summary) => {
        acc[summary.key] = summary.name.replace(' Summary', '');
        return acc;
    }, {} as Record<SummaryType, string>);

    public viewModel$ = this.store.select(createSelector({
        selectSummaryIndex: selectSummaryIndex,
        summaries: selectSummaryEditorValues,
        downloadButtonDisabled: createSelector(
            selectSummaryIndex,
            selectSummaryEditorValues,
            (summaryIndex, summaries) => {
                const summaryKey = JOB_SUMMARIES[summaryIndex]?.key;

                if (!summaryKey) {
                    return true;
                }

                return isDeltaBlank(summaries[summaryKey]);
            },
        ),
    }));

    public actions = JobSummaryActions;
    public newOpportunityActions = NewOpportunityActions;

    private _moveBackToSummaryTab() {
        const focusableElements = Array.from(document.querySelectorAll<HTMLElement>('div[tabindex]'))
            .filter(el => el.tabIndex >= 0);

        focusableElements.sort((a, b) => a.tabIndex - b.tabIndex);

        const nextElement = focusableElements[0];

        nextElement?.focus();
    }
}
